import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AuthLayout, Field } from '@/components/AuthLayout'
import { useAuth } from '@/store/auth'

export function ForgotPasswordPage() {
  const { error, forgotPassword, clearError } = useAuth()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await forgotPassword(email)
      setSent(true)
    } catch {
      // The store surfaces the message; nothing else to do here.
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`If an account exists for ${email}, a reset link is on its way.`}
      >
        <Link
          to="/login"
          className="block w-full rounded-md bg-brand py-3 text-center text-[16px] font-extrabold text-white shadow-[0_14px_28px_rgba(15,118,110,.24)] transition hover:bg-brand-dark"
        >
          Back to sign in
        </Link>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a link to choose a new one." assurance="Secure password reset" backTo="/login">
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(v) => {
            clearError()
            setEmail(v)
          }}
          autoComplete="email"
          placeholder="you@example.com"
          required
        />

        {error && (
          <p
            role="alert"
            className="rounded-lg border border-danger/25 bg-danger/[0.06] px-3 py-2.5 text-[13px] leading-relaxed text-danger"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-brand py-3 text-[16px] font-extrabold text-white shadow-[0_14px_28px_rgba(15,118,110,.24)] transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <p className="mt-5 text-center text-[13px] text-brand-muted">
        Remembered it?{' '}
        <Link to="/login" className="font-semibold text-brand hover:text-brand-dark">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  )
}
